import {Injectable, signal} from '@angular/core';
import {environment} from '../../environments/environment';
import {Router} from "@angular/router";
import {SocketIoFactory} from './socket-io-factory.service';
import {ResilientSocketConnection} from './resilient-socket-connection';

interface AuthStatusEvent {
    keycloak: boolean;
    datev: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class AuthStatusService extends ResilientSocketConnection<AuthStatusEvent> {
    private baseUrl = `${environment.apiUrl}`;
    private authStatus = signal<boolean>(false);
    private user = signal<User | null>(null);

    constructor(private router: Router, socketFactory: SocketIoFactory) {
        super(socketFactory);
        this.initConnection();
    }

    getStatus() {
        return this.authStatus.asReadonly();
    }

    getUser() {
        return this.user.asReadonly();
    }

    protected getWsUrl(): string {
        if (environment.wsUrl) {
            return environment.wsUrl;
        }
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${protocol}//${window.location.host}/ws/auth`;
    }

    protected getEventName(): string {
        return 'auth-status';
    }

    protected handleEvent(data: AuthStatusEvent): void {
        this.handleAuthUpdate(data?.keycloak ?? false);
    }

    protected getPollIntervalMs(): number {
        return 5000;
    }

    // Beim (erneuten) Start des Polling-Fallbacks sofort einmal prüfen, statt auf das erste
    // Intervall-Tick zu warten - siehe pollStatus()/fetchUserInfo() unten. Das Intervall wird
    // von der Basisklasse erst nach Auflösung dieses Promise scharf geschaltet, damit ein
    // etwaiger stopPolling()-Aufruf innerhalb von pollStatus() (nicht authentifiziert) nicht
    // durch das danach gesetzte Intervall sofort wieder aufgehoben wird.
    protected override onPollingStarted(): Promise<void> {
        return this.pollStatus().then(() => {
            this.fetchUserInfo();
        });
    }

    protected poll(): Promise<void> {
        return this.pollStatus().then(() => {
        });
    }

    private handleAuthUpdate(authenticated: boolean) {
        if (authenticated) {
            this.authStatus.set(true);
            if (this.router.url === '/login') {
                this.router.navigate(['/']);
            }
            if (!this.user()) {
                this.fetchUserInfo();
            }
        } else {
            this.authStatus.set(false);
            this.user.set(null);
            this.router.navigate(['/login']);
        }
    }

    // Für den Polling-Fallback: aktualisiert den Status wie checkStatus(), navigiert aber
    // zusätzlich selbst (zu / bzw. /login), da das Polling - anders als der AuthGuard, der
    // seine Navigation über einen zurückgegebenen UrlTree steuert - außerhalb jeder
    // Router-Navigation läuft und daher sicher direkt navigieren kann.
    private async pollStatus(): Promise<boolean> {
        const authenticated = await this.checkStatus();
        if (authenticated) {
            if (this.router.url === '/login') {
                this.router.navigate(['/']);
            }
        } else {
            this.stopPolling();
            this.router.navigate(['/login']);
        }
        return authenticated;
    }

    private fetchUserInfo() {
        fetch(`${this.baseUrl}/user`).then(response => {
            if (response.ok) {
                response.json().then(userInfo => {
                    this.user.set(userInfo);
                })
            } else {
                this.user.set(null);
            }
        })
    }

    // Rein lesend/aktualisierend: setzt nur den authStatus-Signal-Wert, ohne Navigations-
    // Seiteneffekte. Wird jetzt auch aus AuthGuard.canActivate() heraus aufgerufen -
    // ein router.navigate() an dieser Stelle würde während einer gerade laufenden
    // Navigationsauflösung eine zweite, konkurrierende Navigation auslösen (abgebrochene/
    // zirkuläre Navigationen). Navigations-Entscheidungen trifft für das Polling pollStatus(),
    // für den Guard der Guard selbst (über einen zurückgegebenen UrlTree).
    public async checkStatus(): Promise<boolean> {
        try {
            const response = await fetch(this.baseUrl + "/is-authenticated");
            const authenticated = response.ok;
            this.authStatus.set(authenticated);
            return authenticated;
        } catch {
            this.authStatus.set(false);
            return false;
        }
    }
}

export class User {
    public username: string = "";
    public consultant_id: string = "";
    public client_id: string = "";
}
